import React, { useState, useEffect } from 'react';
import type { Bar } from '../hooks/useBars';
import { supabase } from '../lib/supabase';
import { logAction, getCurrentAdmin } from '../services/logger';
import { X, MapPin, Image, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface Props {
  bar: Bar | null;
  isNew?: boolean;
  onSave: (id: string | null, data: Partial<Bar>) => Promise<void>;
  onDelete?: (bar: Bar) => void;
  canDelete?: boolean;
  onClose: () => void;
}

function probLabel(p: number): string {
  if (p >= 90) return 'Quasi certaine';
  if (p >= 80) return 'Fiable';
  if (p >= 60) return 'Probable';
  if (p >= 40) return 'Possible';
  return 'Incertain';
}

function probColor(p: number): string {
  if (p >= 80) return 'var(--green)';
  if (p >= 60) return 'var(--orange)';
  return 'var(--red)';
}

interface BarDiffusion {
  id: string;
  match_id: string;
  status: string;
  confirmed_at: string | null;
  matches: { id: string; home_team: string; away_team: string; match_date: string; match_time: string; competition: string } | null;
}

export function BarModal({ bar, isNew, onSave, onDelete, canDelete, onClose }: Props) {
  const [tab, setTab] = useState<'info' | 'diffusions'>('info');
  const [form, setForm] = useState({
    name: '', address: '', city: '', phone: '', website: '', instagram: '',
    description: '', notes: '', image_url: '',
    probability: 50, tv_channels: '', screens_count: 0,
    google_rating: 0, google_reviews: 0, google_place_id: '',
    latitude: 0, longitude: 0, is_online: true,
  });
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [imgValid, setImgValid] = useState<boolean | null>(null);

  // Diffusions tab state
  const [barDiffusions, setBarDiffusions] = useState<BarDiffusion[]>([]);
  const [loadingDiffs, setLoadingDiffs] = useState(false);
  const [availableMatches, setAvailableMatches] = useState<any[]>([]);
  const [addMatchId, setAddMatchId] = useState('');
  const [addStatus, setAddStatus] = useState('probable');

  useEffect(() => {
    if (bar) {
      setForm({
        name: bar.name || '', address: bar.address || '', city: bar.city || '',
        phone: bar.phone || '', website: bar.website || '', instagram: bar.instagram || '',
        description: bar.description || '', notes: bar.notes || '',
        image_url: bar.image_url || '',
        probability: bar.probability ?? 50, tv_channels: bar.tv_channels || '',
        screens_count: bar.screens_count ?? 0,
        google_rating: bar.google_rating ?? 0, google_reviews: bar.google_reviews ?? 0,
        google_place_id: bar.google_place_id || '',
        latitude: bar.latitude ?? 0, longitude: bar.longitude ?? 0,
        is_online: bar.is_online ?? true,
      });
      loadBarDiffusions(bar.id);
    }
    setImgValid(null);
  }, [bar]);

  const loadBarDiffusions = async (barId: string) => {
    setLoadingDiffs(true);
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('diffusions')
      .select('id, match_id, status, confirmed_at, matches(id, home_team, away_team, match_date, match_time, competition)')
      .eq('bar_id', barId);

    const sorted = ((data || []) as unknown as BarDiffusion[]).sort((a, b) => {
      const da = a.matches?.match_date || ''; const db = b.matches?.match_date || '';
      return da.localeCompare(db) || (a.matches?.match_time || '').localeCompare(b.matches?.match_time || '');
    });
    setBarDiffusions(sorted);

    // Load available matches (not already assigned)
    const assignedIds = sorted.map(d => d.match_id);
    const { data: matchesData } = await supabase.from('matches')
      .select('id, home_team, away_team, match_date, match_time, competition')
      .gte('match_date', today)
      .order('match_date').order('match_time')
      .limit(100);
    setAvailableMatches((matchesData || []).filter(m => !assignedIds.includes(m.id)));
    setLoadingDiffs(false);
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await onSave(bar?.id || null, {
        name: form.name, address: form.address, city: form.city || null,
        phone: form.phone || null, website: form.website || null,
        instagram: form.instagram || null,
        description: form.description || null, notes: form.notes || null,
        image_url: form.image_url || null,
        probability: form.probability, tv_channels: form.tv_channels || null,
        screens_count: form.screens_count || null,
        google_rating: form.google_rating || null, google_reviews: form.google_reviews || null,
        google_place_id: form.google_place_id || null,
        latitude: form.latitude || null, longitude: form.longitude || null,
        is_online: form.is_online,
      });
      onClose();
    } finally { setSaving(false); }
  };

  const handleGeocode = async () => {
    if (!form.address) return;
    setGeocoding(true);
    try {
      const q = encodeURIComponent(form.address + (form.city ? ', ' + form.city : '') + ', France');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, { headers: { 'User-Agent': 'ONPITCH-Admin/1.0' } });
      const data = await res.json();
      if (data?.[0]) setForm(f => ({ ...f, latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }));
    } catch {} finally { setGeocoding(false); }
  };

  const testImage = () => {
    if (!form.image_url) return;
    const img = new window.Image();
    img.onload = () => setImgValid(true);
    img.onerror = () => setImgValid(false);
    img.src = form.image_url;
  };

  const handleAddMatch = async () => {
    if (!addMatchId || !bar) return;
    const admin = getCurrentAdmin();
    const match = availableMatches.find((m: any) => m.id === addMatchId);
    await supabase.from('diffusions').insert({
      bar_id: bar.id, match_id: addMatchId, status: addStatus,
      ...(addStatus !== 'unconfirmed' ? { confirmed_at: new Date().toISOString(), confirmed_by: 'admin' } : {}),
    });
    await logAction('confirm_diffusion', 'diffusion', null, null,
      { bar: bar.name, match: `${match?.home_team} vs ${match?.away_team}`, status: addStatus },
      `${admin?.name} a assigné ${match?.home_team} vs ${match?.away_team} au ${bar.name}`);
    toast.success(`Match ajouté pour ${bar.name}`);
    setAddMatchId('');
    loadBarDiffusions(bar.id);
  };

  const handleRemoveDiffusion = async (diff: BarDiffusion) => {
    if (!bar) return;
    const matchName = diff.matches ? `${diff.matches.home_team} vs ${diff.matches.away_team}` : '?';
    const admin = getCurrentAdmin();
    await supabase.from('diffusions').delete().eq('id', diff.id);
    await logAction('delete_diffusion', 'diffusion', diff.id,
      { bar: bar.name, match: matchName }, null,
      `${admin?.name} a retiré ${matchName} du ${bar.name}`);
    toast.success('Match retiré');
    loadBarDiffusions(bar.id);
  };

  const handleDiffStatusChange = async (diff: BarDiffusion, newStatus: string) => {
    if (!bar) return;
    const admin = getCurrentAdmin();
    const matchName = diff.matches ? `${diff.matches.home_team} vs ${diff.matches.away_team}` : '?';
    await supabase.from('diffusions').update({
      status: newStatus, confirmed_at: new Date().toISOString(), confirmed_by: 'admin',
    }).eq('id', diff.id);
    await logAction('confirm_diffusion', 'diffusion', diff.id,
      { status: diff.status }, { status: newStatus },
      `${admin?.name} a passé ${bar.name} en "${newStatus}" pour ${matchName}`);
    toast.success('Statut mis à jour');
    loadBarDiffusions(bar.id);
  };

  const set = (key: string, val: any) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel slide-in">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent)' }}>{isNew ? 'Ajouter un bar' : `${bar?.name}`}</h2>
          <button onClick={onClose} className="btn-icon"><X size={18} /></button>
        </div>

        {/* Tabs */}
        {!isNew && (
          <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '1px solid var(--border)' }}>
            <button
              onClick={() => setTab('info')}
              style={{
                padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                background: 'none', border: 'none', borderBottom: tab === 'info' ? '2px solid var(--accent)' : '2px solid transparent',
                color: tab === 'info' ? 'var(--accent)' : 'var(--text-muted)',
              }}
            >Informations</button>
            <button
              onClick={() => setTab('diffusions')}
              style={{
                padding: '8px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                background: 'none', border: 'none', borderBottom: tab === 'diffusions' ? '2px solid var(--accent)' : '2px solid transparent',
                color: tab === 'diffusions' ? 'var(--accent)' : 'var(--text-muted)',
              }}
            >Diffusions ({barDiffusions.length})</button>
          </div>
        )}

        {/* TAB: Info */}
        {(tab === 'info' || isNew) && (
          <>
            <div className="modal-section-title">Infos générales</div>
            <div className="form-field">
              <label>Nom du bar</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="form-grid-2">
              <div className="form-field"><label>Adresse</label><input value={form.address} onChange={e => set('address', e.target.value)} /></div>
              <div className="form-field"><label>Ville</label><input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Lille" /></div>
            </div>
            <div className="form-grid-2">
              <div className="form-field"><label>Téléphone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-field"><label>Instagram</label><input value={form.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@..." /></div>
            </div>
            <div className="form-field"><label>Site web</label><input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." /></div>
            <div className="form-field" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={form.is_online} onChange={e => set('is_online', e.target.checked)} id="barOnline" />
              <label htmlFor="barOnline" style={{ margin: 0 }}>Actif / En ligne</label>
            </div>

            <div className="modal-section-title" style={{ marginTop: '20px' }}>Diffusion & Écrans</div>
            <div className="form-field">
              <label>Probabilité : {form.probability}% — <span style={{ color: probColor(form.probability), fontWeight: 600 }}>{probLabel(form.probability)}</span></label>
              <input type="range" min="0" max="100" value={form.probability} onChange={e => set('probability', parseInt(e.target.value))} style={{ width: '100%', accentColor: probColor(form.probability) }} />
            </div>
            <div className="form-grid-2">
              <div className="form-field"><label>Chaînes TV</label><input value={form.tv_channels} onChange={e => set('tv_channels', e.target.value)} placeholder="Canal+, BeIN..." /></div>
              <div className="form-field"><label>Écrans</label><input type="number" min="0" value={form.screens_count} onChange={e => set('screens_count', parseInt(e.target.value) || 0)} /></div>
            </div>

            <div className="modal-section-title" style={{ marginTop: '20px' }}>Photo</div>
            <div className="form-field">
              <label>URL de la photo</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input value={form.image_url} onChange={e => { set('image_url', e.target.value); setImgValid(null); }} placeholder="https://..." style={{ flex: 1 }} />
                <button className="btn btn-outline" onClick={testImage} type="button"><Image size={14} /> Tester</button>
              </div>
              {imgValid === true && <span style={{ fontSize: '11px', color: 'var(--green)', marginTop: '4px', display: 'block' }}>Image valide</span>}
              {imgValid === false && <span style={{ fontSize: '11px', color: 'var(--red)', marginTop: '4px', display: 'block' }}>Image introuvable</span>}
            </div>
            {form.image_url && imgValid !== false && (
              <div style={{ marginBottom: '14px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)', maxHeight: '120px' }}>
                <img src={form.image_url} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover' }} onError={() => setImgValid(false)} />
              </div>
            )}

            <div className="modal-section-title" style={{ marginTop: '20px' }}>Google Maps</div>
            <div className="form-grid-3">
              <div className="form-field"><label>Note</label><input type="number" step="0.1" min="0" max="5" value={form.google_rating} onChange={e => set('google_rating', parseFloat(e.target.value) || 0)} /></div>
              <div className="form-field"><label>Avis</label><input type="number" min="0" value={form.google_reviews} onChange={e => set('google_reviews', parseInt(e.target.value) || 0)} /></div>
              <div className="form-field"><label>Place ID</label><input value={form.google_place_id} onChange={e => set('google_place_id', e.target.value)} /></div>
            </div>
            <div className="form-grid-2">
              <div className="form-field"><label>Latitude</label><input type="number" step="0.0001" value={form.latitude} onChange={e => set('latitude', parseFloat(e.target.value) || 0)} /></div>
              <div className="form-field"><label>Longitude</label><input type="number" step="0.0001" value={form.longitude} onChange={e => set('longitude', parseFloat(e.target.value) || 0)} /></div>
            </div>
            <button className="btn btn-outline" onClick={handleGeocode} disabled={geocoding} type="button">
              <MapPin size={14} /> {geocoding ? 'Géocodage...' : 'Géocoder l\'adresse'}
            </button>

            <div className="modal-section-title" style={{ marginTop: '20px' }}>Notes</div>
            <div className="form-field"><label>Description (visible app)</label><textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} style={{ resize: 'vertical' }} /></div>
            <div className="form-field"><label>Notes internes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ resize: 'vertical' }} placeholder="Notes admin..." /></div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
              <div>{!isNew && canDelete && onDelete && bar && (<button className="btn btn-danger" onClick={() => onDelete(bar)} type="button">Supprimer</button>)}</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline" onClick={onClose}>Annuler</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Sauvegarde...' : isNew ? 'Ajouter' : 'Sauvegarder'}</button>
              </div>
            </div>
          </>
        )}

        {/* TAB: Diffusions */}
        {tab === 'diffusions' && !isNew && bar && (
          <>
            {/* Add match form */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'end' }}>
              <div className="form-field" style={{ marginBottom: 0, flex: 2 }}>
                <label>Ajouter un match</label>
                <select value={addMatchId} onChange={e => setAddMatchId(e.target.value)} className="filter-select" style={{ width: '100%' }}>
                  <option value="">Choisir un match...</option>
                  {availableMatches.map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {m.home_team} vs {m.away_team} ({format(new Date(m.match_date + 'T00:00:00'), 'dd/MM', { locale: fr })} {m.match_time})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0, flex: 1 }}>
                <label>Statut</label>
                <select value={addStatus} onChange={e => setAddStatus(e.target.value)} className="filter-select" style={{ width: '100%' }}>
                  <option value="unconfirmed">Non vérifié</option>
                  <option value="probable">Probable</option>
                  <option value="confirmed">Confirmé</option>
                </select>
              </div>
              <button className="btn btn-primary" onClick={handleAddMatch} style={{ height: '36px' }}><Plus size={14} /></button>
            </div>

            {/* List */}
            {loadingDiffs ? (
              <div className="skeleton-list">{[1,2,3].map(i => <div key={i} className="skeleton-row" />)}</div>
            ) : barDiffusions.length === 0 ? (
              <p className="muted">Aucun match assigné</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {barDiffusions.map(d => {
                  const m = d.matches;
                  const isPast = m && m.match_date < new Date().toISOString().split('T')[0];
                  return (
                    <div key={d.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)',
                      background: isPast ? 'rgba(0,0,0,0.02)' : 'transparent',
                      opacity: isPast ? 0.5 : 1,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>{m?.home_team} vs {m?.away_team}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {m && format(new Date(m.match_date + 'T00:00:00'), 'EEE d MMM', { locale: fr })} · {m?.match_time} · {m?.competition}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <select
                          className={`status-select ${d.status}`}
                          value={d.status}
                          onChange={e => handleDiffStatusChange(d, e.target.value)}
                        >
                          <option value="unconfirmed">Non vérifié</option>
                          <option value="probable">Probable</option>
                          <option value="confirmed">Confirmé</option>
                        </select>
                        <button className="btn-icon danger" onClick={() => handleRemoveDiffusion(d)} title="Retirer">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
              {barDiffusions.length} matchs assignés · {barDiffusions.filter(d => d.status === 'confirmed').length} confirmés
            </div>
          </>
        )}
      </div>
    </div>
  );
}
